import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { IconInfoCircle } from "@tabler/icons-react"
import { type FC, Fragment, ReactNode } from "react"

import { type Video as VideoType } from "@forento/core/models/video"
import { toDateTimeString } from "@forento/core/utilities/date"
import HostedVideoPlayer from "@forento/ui/components/HostedVideoPlayer"
import Tooltip from "@forento/ui/components/Tooltip"

import Button from "~/components/Button"
import { EditIcon, UploadIcon } from "~/components/Icon"
import ProductListRow from "~/components/ProductListRow"
import routes from "~/utilities/routes"

const Video: FC<{ video: VideoType }> = ({ video }) => {
	const { setNodeRef, transform, transition, attributes, listeners } = useSortable({ id: video.id })

	const subtitleParts: ReactNode[] = []
	if (video.playableDate === null) {
		subtitleParts.push(<span className="text-light text-xs">Processing...</span>)
	}
	if (video.playableDate) {
		subtitleParts.push(<span className="text-light text-xs">{toDateTimeString(video.playableDate)}</span>)
	}
	if (video.pauseDate) {
		subtitleParts.push(<span className="text-danger text-xs">Unlisted on {toDateTimeString(video.pauseDate)}</span>)
	}
	if (video.host === "bunny") {
		subtitleParts.push(
			<span className="text-light inline-flex items-center gap-1 text-xs">
				{video.watchCount} {video.watchCount === 1 ? "view" : "views"}
				<Tooltip tooltip="Views are counted from 2026" inline className="inline-flex cursor-help">
					<IconInfoCircle className="size-4 shrink-0 opacity-70" />
				</Tooltip>
			</span>,
		)
	}

	return (
		<ProductListRow
			ref={setNodeRef}
			title={video.name}
			subtitle={subtitleParts.map((part, index) => (
				<Fragment key={index}>
					{part}
					{index < subtitleParts.length - 1 && <div className="size-1 rounded-full bg-slate-300" />}
				</Fragment>
			))}
			thumbnail={
				video.uploadCompleteDate !== null ? (
					<HostedVideoPlayer video={video} />
				) : (
					<Button variant="primary" onClick={routes.video.upload(video.id)}>
						<UploadIcon className="size-5" />
						Upload
					</Button>
				)
			}
			actions={[{ id: "edit", tooltip: "Edit", icon: EditIcon, href: routes.video.edit(video.id) }]}
			dragHandle={{ attributes, listeners }}
			style={{ transform: CSS.Transform.toString(transform), transition }}
		/>
	)
}

export default Video
